import { Icon } from 'design-system';
import {
  BASE_SIZE,
  BORDER_WIDTH,
  FONT_FAMILY,
  FONT_SIZE,
  PADDING,
  useTheme,
} from 'design-system/theme';
import {
  type CountryCode,
  parsePhoneNumberFromString,
} from 'libphonenumber-js';
import { useEffect, useState } from 'react';
import PhoneInput, {
  getCountryByPhoneNumber,
  type ICountry,
} from 'react-native-international-phone-number';
import { strings } from 'strings';

import { Wrapper } from '../internal';

export interface PhoneNumberInputProps {
  label?: string;
  value: string;
  errors?: string;
  placeholder?: string;
  onChange: (value?: string) => void;
  key?: string;
}

const formatNationalNumber = (fullNumber: string) => {
  const phone = parsePhoneNumberFromString(fullNumber);
  return phone?.formatNational() ?? '';
};

export const PhoneNumberInput: React.FC<PhoneNumberInputProps> = ({
  value,
  onChange,
  label = 'Phone Number',
  errors,
  placeholder,
  key,
}) => {
  const theme = useTheme();
  const [selectedCountry, setSelectedCountry] = useState<ICountry>();
  const [inputValue, setInputValue] = useState<string>(value);
  const [localError, setLocalError] = useState<string | undefined>();
  useEffect(() => {
    // Sync external value to input field
    if (value && value !== `${selectedCountry?.callingCode}${inputValue}`) {
      const country = getCountryByPhoneNumber(value);
      setSelectedCountry(country);
      const formatted = formatNationalNumber(value);
      setInputValue(formatted);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleInputValue = () => {
    if (!inputValue || !selectedCountry) {
      return;
    }

    const parsedPhoneNumber = parsePhoneNumberFromString(
      inputValue,
      selectedCountry.cca2 as CountryCode,
    );

    if (parsedPhoneNumber?.isValid()) {
      setLocalError(undefined);
      onChange(parsedPhoneNumber.number);
    } else {
      setLocalError(strings.PhoneNumberLineError);
    }
  };

  return (
    <Wrapper label={label} error={errors || localError}>
      <PhoneInput
        key={key}
        value={inputValue}
        onChangePhoneNumber={setInputValue}
        selectedCountry={selectedCountry}
        defaultValue={value}
        onChangeSelectedCountry={setSelectedCountry}
        onBlur={handleInputValue}
        defaultCountry="ZA"
        placeholder={placeholder}
        customCaret={<Icon name="keyboard-arrow-down" color="text-default" />}
        phoneInputStyles={{
          container: {
            backgroundColor: theme.color['base'],
            paddingVertical: PADDING['2xs'],
            paddingHorizontal: PADDING.xs,
            width: '100%',
            borderWidth: 0,
          },
          flagContainer: {
            backgroundColor: theme.color['base'],
            justifyContent: 'center',
          },
          divider: {
            backgroundColor: theme.color['divider'],
          },
          callingCode: {
            fontSize: FONT_SIZE.body,
            fontFamily: FONT_FAMILY.regular,
            color: theme.color['text-default'],
          },
          input: {
            color: theme.color['text-default'],
          },
        }}
        modalStyles={{
          modal: {
            backgroundColor: theme.color['base'],
            borderWidth: BORDER_WIDTH[2],
            width: 'auto',
          },
          searchInput: {
            borderRadius: BASE_SIZE[8],
            color: theme.color['text-default'],
            backgroundColor: theme.color['divider'],
            paddingHorizontal: BASE_SIZE[12],
            height: BASE_SIZE[48],
          },
          countryButton: {
            backgroundColor: theme.color['divider'],
            paddingVertical: PADDING['2xs'],
            paddingHorizontal: PADDING.xs,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
          },
          callingCode: {
            fontSize: FONT_SIZE.body,
            fontFamily: FONT_FAMILY.regular,
            color: theme.color['text-default'],
          },
          countryName: {
            fontSize: FONT_SIZE.body,
            fontFamily: FONT_FAMILY.regular,
            color: theme.color['text-default'],
          },
        }}
      />
    </Wrapper>
  );
};
